import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, ShieldAlert } from 'lucide-react';
import { TranslationNS } from '@gokrew/models/constants';
import { useAuth } from '@/context/AuthContext';
import styles from './ImpersonationBanner.module.css';

/**
 * Shown on every page of a super admin mock login ("Open as HR" from the
 * admin portal, or a mock login from the user explorer): whose company and
 * account the tab is signed in to, that every change is logged under the
 * super admin, and a way out. Exit ends the mock session server side.
 */
export const ImpersonationBanner = memo(() => {
  const { t } = useTranslation(TranslationNS.Dashboard);
  const { user, companyName, endMockSession } = useAuth();
  const navigate = useNavigate();

  const name =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    '';

  const exit = useCallback(() => {
    navigate('/mock-session-expired?ended=true');
    endMockSession();
  }, [navigate, endMockSession]);

  return (
    <div className={styles.banner} role="status">
      <ShieldAlert className={styles.icon} aria-hidden="true" />
      <p className={styles.text}>
        <span className={styles.title}>
          {companyName
            ? t('impersonation_banner.title', { company: companyName, name })
            : t('impersonation_banner.title_no_company', { name })}
        </span>{' '}
        <span className={styles.body}>{t('impersonation_banner.body')}</span>
      </p>
      <button type="button" className={styles.exit} onClick={exit}>
        <LogOut className={styles.exitIcon} aria-hidden="true" />
        {t('impersonation_banner.exit')}
      </button>
    </div>
  );
});

ImpersonationBanner.displayName = 'ImpersonationBanner';
